import type { AxiosResponse } from "axios";
import { IAccount } from "types/account";
import { IPaymentIntentResponse } from "types/Api/Payment/paymentIntentReponse";
import { IBiller } from "types/biller";
import { IBillerConfiguration } from "types/billerConfiguration";
import { Payable } from "types/payable";
import { IPayableSessionData } from "types/payableSession";
import { IPaymentSettings } from "types/paymentSettings";
import { IRetailer } from "types/retailer";
import { SessionSupportedLanguages } from "types/session";
import { IStoreLocation } from "types/store-location";
import { ITenantSnapshot } from "types/tenantSnapshot";
import {
    sendDeleteRequest,
    sendDeleteRequestWithPayload,
    sendGetRequest,
    sendPostRequest,
    sendPutRequest,
} from "../lib";
import routes from "../routing";

export const getConfiguration = (
  subDomain: string
): Promise<AxiosResponse<IBiller>> =>
  sendGetRequest(routes.ezPay.getConfiguration(subDomain));

export const postValidation = (
  instanceId: string,
  language: string,
  domain: string,
  fingerprint: string,
  userAgent: string,
  validationRequest: unknown
): Promise<AxiosResponse> => {
  return sendPostRequest(
    validationRequest,
    routes.ezPay.postValidation(instanceId),
    {
      headers: {
        channel: "Web",
        language: language,
        domain: domain,
        deviceFingerprint: fingerprint,
        userAgent: userAgent,
      },
      withCredentials: true,
    }
  );
};

export const postValidationV2 = (
  spaceKrn: string,
  language: string,
  domain: string,
  fingerprint: string,
  userAgent: string,
  validationRequest: unknown
): Promise<AxiosResponse> => {
  return sendPostRequest(
    validationRequest,
    routes.ezPay.postValidationV2(spaceKrn),
    {
      headers: {
        channel: "Web",
        language: language,
        domain: domain,
        deviceFingerprint: fingerprint,
        userAgent: userAgent,
      },
      withCredentials: true,
    }
  );
};

export const postBalance = (
  fingerprint: string,
  userAgent: string
): Promise<AxiosResponse> => {
  return sendPostRequest(null, routes.ezPay.postBalance(), {
    headers: {
      deviceFingerprint: fingerprint,
      userAgent: userAgent,
    },
    withCredentials: true,
  });
};

export const postAccountMessages = (): Promise<AxiosResponse> => {
  return sendPostRequest(null, routes.ezPay.postAccountMessages(), {
    withCredentials: true,
  });
};

export const postEmail = (
  instanceId: string,
  clientId: string,
  email: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postEmail(email), {
    headers: { instanceId: instanceId, "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const postEmailV2 = (
  spaceKrn: string,
  clientId: string,
  email: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postEmailV2(email), {
    headers: { spaceKrn: spaceKrn, "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const postPayableEmail = (
  spaceKrn: string,
  clientId: string,
  email: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postPayableEmail(email), {
    headers: { spaceKrn: spaceKrn, "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const postSms = (
  instanceId: string,
  clientId: string,
  phoneno: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postSms(phoneno), {
    headers: { instanceId: instanceId, "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const postSmsV2 = (
  spaceKrn: string,
  clientId: string,
  phoneno: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postSmsV2(phoneno), {
    headers: { spaceKrn: spaceKrn, "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const postPayableSms = (
  spaceKrn: string,
  clientId: string,
  phoneno: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postPayableSms(phoneno), {
    headers: { spaceKrn: spaceKrn, "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const getBarcode = (
  clientId: string,
  realm: string
): Promise<AxiosResponse<string>> =>
  sendGetRequest(routes.ezPay.getBarcode(), {
    headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
    withCredentials: true,
  });

export const getPayableBarcode = (
  payableKrn: string,
  realm: string
): Promise<AxiosResponse<string>> => {
  return sendGetRequest(routes.ezPay.getPayableBarcode(payableKrn), {
    headers: { "Kubra-Realm": realm },
    withCredentials: true,
  });
};

export const postInitiateIntent = (
  clientId: string,
  realm: string,
  fingerprint: string,
  userAgent: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postInitiateIntent(), {
    headers: {
      "Kubra-ClientId": clientId,
      "Kubra-Realm": realm,
      deviceFingerPrint: fingerprint,
      userAgent: userAgent,
    },
    withCredentials: true,
  });
};

export const postPrepareIntent = (
  clientId: string,
  paymentIntentId: string,
  prepareRequest: unknown
): Promise<AxiosResponse> => {
  return sendPostRequest(
    prepareRequest,
    routes.ezPay.postPrepareIntent(paymentIntentId),
    { headers: { "Kubra-ClientId": clientId }, withCredentials: true }
  );
};

export const postProcessIntent = (
  clientId: string,
  paymentIntentId: string,
  processRequest: unknown,
  language: string
): Promise<AxiosResponse> => {
  return sendPostRequest(
    processRequest,
    routes.ezPay.postProcessIntent(paymentIntentId),
    {
      headers: { "Kubra-ClientId": clientId, language: language },
      withCredentials: true,
    }
  );
};

export const getPaymentIntent = (
  clientId: string,
  paymentIntentId: string
): Promise<IPaymentIntentResponse> => {
  return sendGetRequest(routes.ezPay.getPaymentIntent(paymentIntentId), {
    headers: { "Kubra-ClientId": clientId },
  });
};

interface GetPaymentParams {
  deviceFingerprint: string;
  userAgent: string;
  domain: string;
  instanceId: string;
  paymentId: string;
  language: string;
  clientId: string;
  paymentRequest: any;
}

export const getPayment = ({
  deviceFingerprint,
  userAgent,
  domain,
  instanceId,
  paymentId,
  language,
  clientId,
  paymentRequest,
}: GetPaymentParams): Promise<AxiosResponse> => {
  return sendPostRequest(
    paymentRequest,
    routes.ezPay.getPayment(paymentId, instanceId),
    {
      withCredentials: true,
      headers: {
        "Kubra-ClientId": clientId,
        userAgent: userAgent,
        domain: domain,
        deviceFingerPrint: deviceFingerprint,
        language: language,
      },
    }
  );
};

interface GetPaymentV2Params {
  deviceFingerprint: string;
  userAgent: string;
  domain: string;
  spaceKrn: string;
  paymentId: string;
  language: string;
  clientId: string;
  paymentRequest: any;
}

export const getPaymentV2 = ({
  deviceFingerprint,
  userAgent,
  domain,
  spaceKrn,
  paymentId,
  language,
  clientId,
  paymentRequest,
}: GetPaymentV2Params): Promise<AxiosResponse> => {
  return sendPostRequest(
    paymentRequest,
    routes.ezPay.getPaymentV2(paymentId, spaceKrn),
    {
      withCredentials: true,
      headers: {
        "Kubra-ClientId": clientId,
        userAgent: userAgent,
        domain: domain,
        deviceFingerPrint: deviceFingerprint,
        language: language,
      },
    }
  );
};

export const getPayableLanding = (
  deviceFingerprint: string,
  userAgent: string,
  domain: string,
  spaceKrn: string,
  language: string,
  clientId: string,
  paymentRequest: any
): Promise<AxiosResponse> => {
  return sendPostRequest(
    paymentRequest,
    routes.ezPay.getPayableLanding(spaceKrn),
    {
      withCredentials: true,
      headers: {
        "Kubra-ClientId": clientId,
        userAgent: userAgent,
        domain: domain,
        deviceFingerPrint: deviceFingerprint,
        language: language,
      },
    }
  );
};

export const getPayableBarcodeLanding = (
  deviceFingerprint: string,
  userAgent: string,
  domain: string,
  spaceKrn: string,
  language: string,
  clientId: string,
  paymentRequest: any
): Promise<AxiosResponse> => {
  return sendPostRequest(
    paymentRequest,
    routes.ezPay.getPayableBarcodeLanding(spaceKrn),
    {
      withCredentials: true,
      headers: {
        "Kubra-ClientId": clientId,
        userAgent: userAgent,
        domain: domain,
        deviceFingerPrint: deviceFingerprint,
        language: language,
      },
    }
  );
};

export const getTenantSnapshot = (
  tenantId: string
): Promise<AxiosResponse<ITenantSnapshot>> =>
  sendGetRequest(routes.ezPay.getTenantSnapshot(tenantId));

export const getPaymentSettings = (
  snapshotId: string
): Promise<AxiosResponse<IPaymentSettings>> =>
  sendGetRequest(routes.ezPay.getPaymentSettings(snapshotId));

export const getAccountFromSession = (domain: string): Promise<IAccount> =>
  sendGetRequest(routes.ezPay.getAccountFromSession(), {
    withCredentials: true,
    headers: {
      domain: domain,
    },
  });

export const logout = (): Promise<AxiosResponse> =>
  sendGetRequest(routes.ezPay.logout(), { withCredentials: true });

export const updateSessionLanguage = (
  language: SessionSupportedLanguages
): Promise<AxiosResponse> =>
  sendPutRequest({}, routes.ezPay.updateSessionLanguage(language), {
    withCredentials: true,
  });

export const validateApplePayMerchant = (
  valiationRequest: unknown
): Promise<AxiosResponse> =>
  sendPostRequest(valiationRequest, routes.ezPay.validateApplePayMerchant());

export const getWalletItems = (clientId: string): Promise<AxiosResponse> =>
  sendGetRequest(routes.ezPay.getWalletItems(), {
    headers: { "Kubra-ClientId": clientId },
    withCredentials: true,
  });

export const getWalletItemsV2 = (clientId: string): Promise<AxiosResponse> =>
  sendGetRequest(routes.ezPay.getWalletItemsV2(), {
    headers: { "Kubra-ClientId": clientId },
    withCredentials: true,
  });

export const getPayableWalletItems = (
  clientId: string
): Promise<AxiosResponse> =>
  sendGetRequest(routes.ezPay.getPayableWalletItems(), {
    headers: { "Kubra-ClientId": clientId },
    withCredentials: true,
  });

export const deleteWalletItem = (
  clientId: string,
  fundingSourceId: string
): Promise<AxiosResponse> =>
  sendDeleteRequest(routes.ezPay.deleteWalletItem(fundingSourceId), {
    headers: { "Kubra-ClientId": clientId },
    withCredentials: true,
  });

export const deleteWalletItemV2 = (
  clientId: string,
  fundingSourceId: string
): Promise<AxiosResponse> => {
  return sendDeleteRequest(routes.ezPay.deleteWalletItemV2(fundingSourceId), {
    headers: { "Kubra-ClientId": clientId },
    withCredentials: true,
  });
};

export const deletePayableWalletItem = (
  clientId: string,
  fundingSourceId: string
): Promise<AxiosResponse> => {
  return sendDeleteRequest(
    routes.ezPay.deletePayableWalletItem(fundingSourceId),
    {
      headers: { "Kubra-ClientId": clientId },
      withCredentials: true,
    }
  );
};

export const deletePaymentDueAlerts = (
  unenrollRequest: unknown,
  clientId: string,
  realm: string
): Promise<AxiosResponse> => {
  return sendDeleteRequestWithPayload(routes.ezPay.deletePaymentDueAlerts(), {
    unenrollRequest,
    headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
  });
};

export const getRecentPayments = (
  count: number,
  clientId: string,
  realm: string
): Promise<unknown> => {
  return sendGetRequest(routes.ezPay.getRecentPayments(count), {
    headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
    withCredentials: true,
  });
};

export const getUpcomingPayment = (
  clientId: string,
  realm: string
): Promise<unknown> => {
  return sendGetRequest(routes.ezPay.getUpcomingPayment(), {
    headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
    withCredentials: true,
  });
};

export const getPayableRecentPayments = (
  count: number,
  clientId: string,
  realm: string
): Promise<unknown> => {
  return sendGetRequest(routes.ezPay.getPayableRecentPayments(count), {
    headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
    withCredentials: true,
  });
};

export const getPayableUpcomingPayment = (
  clientId: string,
  realm: string
): Promise<unknown> => {
  return sendGetRequest(routes.ezPay.getPayableUpcomingPayment(), {
    headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
    withCredentials: true,
  });
};

export const cancelUpcomingPayment = (
  paymentId: string,
  paymentIntentId: string,
  clientId: string,
  realm: string
): Promise<unknown> => {
  return sendPostRequest(
    { paymentIntentId: paymentIntentId },
    routes.ezPay.postCancelPayment(paymentId),
    {
      headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
      withCredentials: true,
    }
  );
};

export const cancelPayableUpcomingPayment = (
  paymentId: string,
  paymentIntentId: string,
  clientId: string,
  realm: string
): Promise<unknown> => {
  return sendPostRequest(
    { paymentIntentId: paymentIntentId },
    routes.ezPay.postPayableCancelPayment(paymentId),
    {
      headers: { "Kubra-ClientId": clientId, "Kubra-Realm": realm },
      withCredentials: true,
    }
  );
};

export const postDirectLanding = (
  request: unknown,
  instanceId: string,
  language: string,
  domain: string,
  fingerprint: string,
  userAgent: string,
  channel?: string
): Promise<any> => {
  channel = channel ? channel : "Web";
  return sendPostRequest(request, routes.ezPay.postDirectLanding(instanceId), {
    headers: {
      channel: channel,
      language: language,
      domain: domain,
      deviceFingerprint: fingerprint,
      userAgent: userAgent,
    },
    withCredentials: true,
  });
};

export const postDirectLandingV2 = (
  request: unknown,
  spaceKrn: string,
  language: string,
  domain: string,
  fingerprint: string,
  userAgent: string,
  channel?: string
): Promise<any> => {
  channel = channel ? channel : "Web";
  return sendPostRequest(request, routes.ezPay.postDirectLandingV2(spaceKrn), {
    headers: {
      channel: channel,
      language: language,
      domain: domain,
      deviceFingerprint: fingerprint,
      userAgent: userAgent,
    },
    withCredentials: true,
  });
};

export const getCsrDirectLanding = (
  instanceId: string,
  paymentIntentId: string,
  domain: string
): Promise<IAccount> => {
  return sendGetRequest(
    routes.ezPay.getCsrDirectLanding(instanceId, paymentIntentId),
    {
      withCredentials: true,
      headers: {
        domain: domain,
      },
    }
  );
};

export const getPayableCsrDirectLanding = (
  spaceKrn: string,
  paymentIntentId: string,
  domain: string,
  payableKrn?: string
): Promise<any> => {
  return sendGetRequest(
    routes.ezPay.getPayableCsrDirectLanding(
      spaceKrn,
      paymentIntentId,
      payableKrn
    ),
    {
      withCredentials: true,
      headers: {
        domain: domain,
      },
    }
  );
};

export const getExtendedSession = (): Promise<any> => {
  return sendGetRequest(routes.ezPay.getExtendedSession(), {
    withCredentials: true,
  });
};

export const postLookups = (
  spaceKrn: string,
  lookupsRequest: unknown
): Promise<AxiosResponse<Payable[]>> => {
  return sendPostRequest(lookupsRequest, routes.ezPay.postLookups(spaceKrn), {
    withCredentials: true,
  });
};

export const postPayableCreate = (
  typeKrn: string,
  payableRequest: unknown
): Promise<AxiosResponse<unknown>> => {
  return sendPostRequest(
    payableRequest,
    routes.ezPay.postPayableCreate(typeKrn),
    {
      withCredentials: true,
    }
  );
};

export const postPayableUpdate = (
  payableKrn: string
): Promise<AxiosResponse> => {
  return sendPostRequest({}, routes.ezPay.postPayableUpdate(payableKrn), {
    withCredentials: true,
  });
};

export const postExtensionFields = (
  payableKrn: string,
  extensionFieldsRequest: unknown
): Promise<AxiosResponse> => {
  return sendPostRequest(
    extensionFieldsRequest,
    routes.ezPay.postExtensionFields(payableKrn),
    {
      withCredentials: true,
    }
  );
};

export const postPayableInitiateIntent = (
  payableKrn: string,
  fingerprint: string,
  userAgent: string
): Promise<AxiosResponse> => {
  return sendPostRequest(
    {},
    routes.ezPay.postPayableInitiateIntent(payableKrn),
    {
      headers: {
        deviceFingerPrint: fingerprint,
        userAgent: userAgent,
      },
      withCredentials: true,
    }
  );
};

export const postPayablePrepareIntent = (
  payablePrepareRequest: unknown
): Promise<AxiosResponse> => {
  return sendPostRequest(
    payablePrepareRequest,
    routes.ezPay.postPayablePrepareIntent(),
    { withCredentials: true }
  );
};

export const postPayableProcessIntent = (
  payableProcessRequest: unknown,
  language: string
): Promise<AxiosResponse> => {
  return sendPostRequest(
    payableProcessRequest,
    routes.ezPay.postPayableProcessIntent(),
    {
      headers: { language: language },
      withCredentials: true,
    }
  );
};

export const getPayableFromSession = (
  domain: string
): Promise<IPayableSessionData> =>
  sendGetRequest(routes.ezPay.getPayableFromSession(), {
    withCredentials: true,
    headers: {
      domain: domain,
    },
  });

export const updatePayableSessionLanguage = (
  language: SessionSupportedLanguages
): Promise<AxiosResponse> =>
  sendPutRequest({}, routes.ezPay.updatePayableSessionLanguage(language), {
    withCredentials: true,
  });

export const getLocationByGeo = (
  spaceKrn: string,
  latitude: string,
  longitude: string,
  radius: number,
  limit: number,
  realm: string,
  clientId: string
): Promise<IStoreLocation[]> => {
  return sendGetRequest(
    routes.ezPay.getLocationByGeo(
      spaceKrn,
      latitude,
      longitude,
      radius,
      limit
    ),
    {
      headers: {
        "Kubra-Realm": realm,
        "Kubra-ClientId": clientId,
      },
    }
  );
};

export const getLocationByZip = (
  spaceKrn: string,
  zipcode: string,
  limit: number,
  realm: string,
  clientId: string
): Promise<IStoreLocation[]> => {
  return sendGetRequest(routes.ezPay.getLocationByZip(spaceKrn, zipcode, limit), {
    headers: {
      "Kubra-Realm": realm,
      "Kubra-ClientId": clientId,
    },
  });
};

export const getRetailers = (
  spaceKrn: string,
  clientId: string,
  realm: string
): Promise<AxiosResponse<IRetailer>> =>
  sendGetRequest(routes.ezPay.getRetailers(spaceKrn), {
    headers: {
      "Kubra-ClientId": clientId,
      "Kubra-Realm": realm,
    },
  });

export const postApplePass = (
  clientId: string,
  applePassRequest: unknown,
  realm: string
): Promise<AxiosResponse> => {
  return sendPostRequest(applePassRequest, routes.ezPay.postApplePass(), {
    headers: {
      "Kubra-ClientId": clientId,
      "Kubra-Realm": realm,
    },
  });
};

export const postGooglePass = (
  clientId: string,
  googlePassRequest: unknown,
  realm: string
): Promise<AxiosResponse> => {
  return sendPostRequest(googlePassRequest, routes.ezPay.postGooglePass(), {
    headers: {
      "Kubra-ClientId": clientId,
      "Kubra-Realm": realm,
    },
  });
};

export const getBillerConfiguration = (
  clientId: string,
  realm: string
): Promise<AxiosResponse<IBillerConfiguration>> =>
  sendGetRequest(routes.ezPay.getBillerConfiguration(), {
    headers: {
      "Kubra-ClientId": clientId,
      "Kubra-Realm": realm,
    },
  });

export default {
  getConfiguration,
  postValidation,
  postValidationV2,
  postBalance,
  postAccountMessages,
  getPaymentSettings,
  getTenantSnapshot,
  updateSessionLanguage,
  getAccountFromSession,
  logout,
  getBarcode,
  postInitiateIntent,
  postPrepareIntent,
  postProcessIntent,
  getPaymentIntent,
  getPayment,
  getPaymentV2,
  getPayableLanding,
  getPayableBarcodeLanding,
  validateApplePayMerchant,
  getWalletItems,
  getWalletItemsV2,
  getPayableWalletItems,
  deleteWalletItem,
  deleteWalletItemV2,
  deletePayableWalletItem,
  getUpcomingPayment,
  getRecentPayments,
  getPayableUpcomingPayment,
  getPayableRecentPayments,
  cancelUpcomingPayment,
  cancelPayableUpcomingPayment,
  postDirectLanding,
  postDirectLandingV2,
  getCsrDirectLanding,
  getPayableCsrDirectLanding,
  getExtendedSession,
  postLookups,
  postPayableCreate,
  postExtensionFields,
  postPayableUpdate,
  postPayableInitiateIntent,
  postPayablePrepareIntent,
  postPayableProcessIntent,
  getPayableFromSession,
  updatePayableSessionLanguage,
  getLocationByGeo,
  getLocationByZip,
  getRetailers,
  postApplePass,
  postGooglePass,
  getBillerConfiguration,
};
